/**
 * Moderation queue-language: TanStack-backed read + imperative helpers for the
 * caller's own preferred moderation-queue filter language.
 *
 * `useModerationLanguage` reads it reactively; `getModerationLanguageAsync` /
 * `setModerationLanguageAsync` are plain functions for the non-React callers (a
 * queue-filter builder and the Account setting's change handler). All three hit
 * the `moderation.getQueueLanguage` / `moderation.setQueueLanguage` procedures,
 * which are `access: 'admin'` — so `useModerationLanguage` takes `enabled` for
 * call sites that also mount for non-admins.
 *
 * See `./README.md` for the convention this file follows.
 */
import type { IsoLocale } from '@repo/shared/locale';
import { useApiQueryUtils } from 'system/api-client-context';
import { type Datastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { siloParamsOf } from './silo-params';
import { useSiloQuery } from './use-silo-query';

/**
 * The caller's own preferred moderation-queue filter language, as `IsoLocale`
 * or `null` when unset. The server coerces legacy stored values (e.g.
 * `'french_swiss'`) to ISO, so this is always a current locale or `null`.
 *
 * The procedure is `access: 'admin'`, so pass `enabled: false` from any caller
 * that mounts for non-admins too (e.g. the Account screen widget, which renders
 * for everyone) — otherwise the read fires a guaranteed 403.
 */
export function useModerationLanguage({ enabled = true }: { enabled?: boolean } = {}): {
    language: IsoLocale | null;
    isLoading: boolean;
} {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.moderation.getQueueLanguage, enabled });
    return { language: query.data?.language ?? null, isLoading: query.isLoading };
}

/**
 * Read the caller's preferred moderation-queue filter language imperatively
 * (returns `IsoLocale` or `null`). Plain function for the non-React
 * moderation-queue filter builder, which has a `Datastore` but no hook context.
 */
export async function getModerationLanguageAsync(datastore: Datastore): Promise<IsoLocale | null> {
    const { language } = await datastore.getApiClient().moderation.getQueueLanguage(siloParamsOf(datastore));
    return language;
}

/**
 * Set the caller's preferred moderation-queue filter language (`null` clears
 * it), then invalidate the `getQueueLanguage` read so any mounted
 * `useModerationLanguage` refetches. Plain function so the Account setting's
 * change handler can call it directly.
 */
export async function setModerationLanguageAsync(datastore: Datastore, language: IsoLocale | null): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    await datastore.getApiClient().moderation.setQueueLanguage({ siloKey, locale, language });
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    fireAndForget(queryClient.invalidateQueries({ queryKey: utils.moderation.getQueueLanguage.key({ input: { siloKey } }) }));
}
